// API-Token-Verwaltung (MCP Phase A1) — rendered inside Settings.
//
// Personal Access Tokens für MCP-Clients (z. B. Claude Code). Der Klartext-
// Token wird GENAU EINMAL nach dem Erstellen angezeigt (Server speichert nur
// den Hash) — die UI macht das mit einem Copy-Feld + deutlichem Hinweis klar.
// Widerruf ist sofort wirksam. Styling folgt TemplatesSection/SpacesSection.

import { useEffect, useState } from "preact/hooks";
import { Copy, KeyRound, Plus, Trash2 } from "lucide-preact";
import { api } from "../lib/api";
import { toastSuccess, toastError } from "../lib/toast";
import { Modal } from "./Modal";
import { INPUT_CLS } from "./NewProjectModal";

import { Button } from "./ui/Button";

interface ApiTokenItem {
  id: number;
  name: string;
  token_prefix: string;
  scopes: string;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

interface CreatedToken extends ApiTokenItem {
  token: string;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Der fertige Einbinde-Befehl für Claude Code. Ohne Token-Argument wird der
 *  Platzhalter eingesetzt (Sektions-Anleitung); mit Token (frisch erstellt)
 *  ist der Befehl 1:1 kopierbar.
 *
 *  scope "local" (Default von `claude mcp add`) bindet nur im aktuellen
 *  Projektverzeichnis ein, "user" in allen Projekten — beide landen in
 *  `~/.claude.json`, NICHT in einer versionierten Datei. Absichtlich KEIN
 *  "project"-Scope: der schreibt in `.mcp.json` im Repo-Root, das i. d. R.
 *  eingecheckt wird — damit würde das persönliche Bearer-Token in die
 *  Git-Historie und zu jedem Mitentwickler gelangen. */
function mcpAddCommand(scope: "local" | "user", token?: string): string {
  return `claude mcp add --transport http juuplan ${window.location.origin}/api/mcp --header "Authorization: Bearer ${token ?? "<dein-token>"}" --scope ${scope}`;
}

/** Code-Block mit Kopier-Button (rechts oben). */
function CommandBlock({ command }: { command: string }): preact.JSX.Element {
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(command);
      toastSuccess("Befehl kopiert.");
    } catch {
      toastError("Kopieren fehlgeschlagen — bitte manuell markieren.");
    }
  };
  return (
    <div class="relative">
      <code class="block whitespace-pre-wrap break-all rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2 py-1.5 pr-9 text-micro text-[color:var(--color-on-surface)]">
        {command}
      </code>
      <button
        type="button"
        aria-label="Befehl kopieren"
        title="Kopieren"
        onClick={() => void copy()}
        class="absolute top-1 right-1 rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container)]"
      >
        <Copy size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

export function ApiTokensSection(): preact.JSX.Element {
  const [items, setItems] = useState<ApiTokenItem[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<"read" | "read,write">("read");
  const [expiresDays, setExpiresDays] = useState<string>("90");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedToken | null>(null);
  const [confirmDel, setConfirmDel] = useState<number | null>(null);
  const [loadError, setLoadError] = useState(false);

  // `loadError` ist noetig, weil `setItems([])` allein die Anzeige „Keine
  // aktiven Tokens" ergibt — nicht zu unterscheiden von einem gescheiterten
  // Abruf. api() toastet nur Mutationen (MUTATION_METHODS in lib/api.ts).
  const load = (): void => {
    setLoadError(false);
    void api<ApiTokenItem[]>("/api/me/tokens")
      .then(setItems)
      .catch(() => {
        setItems([]);
        setLoadError(true);
        toastError("Token konnten nicht geladen werden.");
      });
  };
  useEffect(load, []);

  const create = async (): Promise<void> => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const days = expiresDays === "" ? null : Number(expiresDays);
      const result = await api<CreatedToken>("/api/me/tokens", {
        method: "POST",
        body: {
          name: name.trim(),
          scopes,
          expires_in_days: days,
        },
      });
      setCreated(result);
      setName("");
      setScopes("read");
      load();
    } catch {
      // api wrapper already toasted
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: number): Promise<void> => {
    try {
      await api<void>(`/api/me/tokens/${id}`, { method: "DELETE" });
      toastSuccess("Token widerrufen.");
      setConfirmDel(null);
      load();
    } catch {
      // api wrapper already toasted
    }
  };

  const copyToken = async (token: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(token);
      toastSuccess("Token kopiert.");
    } catch {
      toastError("Kopieren fehlgeschlagen — bitte manuell markieren.");
    }
  };

  return (
    <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]">
      <div class="flex items-center justify-between px-3 py-2 border-b border-[color:var(--color-outline-variant)]">
        <span class="flex items-center gap-2 text-sm font-medium text-[color:var(--color-on-surface)]">
          <KeyRound size={16} aria-hidden="true" />
          API-Tokens
        </span>
        <button
          type="button"
          onClick={() => {
            setShowCreate(true);
            setCreated(null);
          }}
          class="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)]"
        >
          <Plus size={14} aria-hidden="true" />
          Neues Token
        </button>
      </div>

      {items === null ? (
        <p class="p-3 text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
      ) : loadError ? (
        <p class="p-3 text-xs text-[color:var(--color-error)]">
          Token konnten nicht geladen werden.{" "}
          <button type="button" onClick={load} class="underline">Erneut laden</button>
        </p>
      ) : items.length === 0 ? (
        <p class="p-3 text-xs text-[color:var(--color-on-surface-variant)] italic">
          Keine aktiven Tokens. Erstelle eines, um juuplan z. B. in Claude Code
          als MCP-Server einzubinden.
        </p>
      ) : (
        <ul class="divide-y divide-[color:var(--color-outline-variant)]">
          {items.map((t) => (
            <li key={t.id} class="flex items-center gap-3 px-3 py-2 text-sm">
              <div class="flex-1 min-w-0">
                <span class="block truncate text-[color:var(--color-on-surface)]">
                  {t.name}
                </span>
                <span class="block text-xs text-[color:var(--color-on-surface-variant)]">
                  <code>{t.token_prefix}…</code>
                  {" · "}
                  {t.scopes === "read" ? "Nur lesen" : "Lesen + Schreiben"}
                  {" · zuletzt benutzt: "}
                  {fmtDate(t.last_used_at)}
                  {" · läuft ab: "}
                  {t.expires_at ? fmtDate(t.expires_at) : "nie"}
                </span>
              </div>
              {confirmDel === t.id ? (
                <div class="flex items-center gap-2">
                  <Button
                    variant="destructive"
                    size="xs"
                    type="button"
                    onClick={() => void revoke(t.id)}
                  >
                    Widerrufen
                  </Button>
                  <button
                    type="button"
                    onClick={() => setConfirmDel(null)}
                    class="rounded px-2 py-1 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
                  >
                    Abbrechen
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  aria-label={`Token ${t.name} widerrufen`}
                  onClick={() => setConfirmDel(t.id)}
                  class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-error)] hover:bg-[color:var(--color-surface-container-low)]"
                >
                  <Trash2 size={16} aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Einbinde-Anleitung: 1:1 kopierbare Befehle (URL automatisch korrekt);
          nur <dein-token> muss durch ein erstelltes Token ersetzt werden. Zwei
          Varianten je nach Scope — "project" absichtlich nicht angeboten
          (würde das Token in eine versionierte .mcp.json schreiben). */}
      <div class="border-t border-[color:var(--color-outline-variant)] px-3 py-2.5">
        <p class="mb-1.5 text-xs text-[color:var(--color-on-surface-variant)]">
          Einbinden in Claude Code (Terminal) — <code>&lt;dein-token&gt;</code>{" "}
          durch ein erstelltes Token ersetzen:
        </p>
        <p class="mt-2 mb-1 text-xs text-[color:var(--color-on-surface-variant)]">
          Nur für dieses Projekt:
        </p>
        <CommandBlock command={mcpAddCommand("local")} />
        <p class="mt-2 mb-1 text-xs text-[color:var(--color-on-surface-variant)]">
          Für alle Projekte (user-weit):
        </p>
        <CommandBlock command={mcpAddCommand("user")} />
      </div>

      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Neues API-Token">
          {created ? (
            <div class="flex flex-col gap-3">
              <p class="text-sm text-[color:var(--color-on-surface)]">
                Token <strong>{created.name}</strong> erstellt. Kopiere es
                jetzt — es wird <strong>nie wieder angezeigt</strong>.
              </p>
              <div class="flex items-center gap-2">
                <code class="flex-1 min-w-0 truncate rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2 py-1.5 text-xs">
                  {created.token}
                </code>
                <button
                  type="button"
                  onClick={() => void copyToken(created.token)}
                  class="flex items-center gap-1 rounded px-2 py-1.5 text-xs font-medium text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)]"
                >
                  <Copy size={14} aria-hidden="true" />
                  Kopieren
                </button>
              </div>
              <p class="text-xs text-[color:var(--color-on-surface-variant)]">
                Einbinden in Claude Code — Befehl enthält das Token, einfach
                kopieren und im Terminal ausführen:
              </p>
              <p class="text-xs text-[color:var(--color-on-surface-variant)]">
                Nur für dieses Projekt:
              </p>
              <CommandBlock command={mcpAddCommand("local", created.token)} />
              <p class="text-xs text-[color:var(--color-on-surface-variant)]">
                Für alle Projekte (user-weit):
              </p>
              <CommandBlock command={mcpAddCommand("user", created.token)} />
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                class="self-end rounded px-3 py-1.5 text-sm font-medium text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)]"
              >
                Fertig
              </button>
            </div>
          ) : (
            <div class="flex flex-col gap-3">
              <label class="flex flex-col gap-1 text-sm text-[color:var(--color-on-surface)]">
                Name
                <input
                  type="text"
                  value={name}
                  onInput={(e) => setName((e.target as HTMLInputElement).value)}
                  placeholder="z. B. Claude Code Laptop"
                  maxLength={100}
                  class={INPUT_CLS}
                />
              </label>
              <label class="flex flex-col gap-1 text-sm text-[color:var(--color-on-surface)]">
                Berechtigung
                <select
                  value={scopes}
                  onChange={(e) =>
                    setScopes(
                      (e.target as HTMLSelectElement).value as "read" | "read,write"
                    )
                  }
                  class={INPUT_CLS}
                >
                  <option value="read">Nur lesen</option>
                  <option value="read,write">Lesen + Schreiben</option>
                </select>
              </label>
              <label class="flex flex-col gap-1 text-sm text-[color:var(--color-on-surface)]">
                Gültigkeit
                <select
                  value={expiresDays}
                  onChange={(e) =>
                    setExpiresDays((e.target as HTMLSelectElement).value)
                  }
                  class={INPUT_CLS}
                >
                  <option value="30">30 Tage</option>
                  <option value="90">90 Tage</option>
                  <option value="365">1 Jahr</option>
                  <option value="">Ohne Ablauf</option>
                </select>
              </label>
              <p class="text-xs text-[color:var(--color-on-surface-variant)]">
                {scopes === "read,write" ? (
                  <>
                    <strong>Lesen + Schreiben:</strong> der KI-Client kann in
                    deinem Namen Projekte, Phasen und Aufgaben anlegen und
                    ändern — immer nur dort, wo du selbst Schreibrechte hast.
                  </>
                ) : (
                  <>
                    Das Token erhält Lesezugriff auf alles, was du selbst sehen
                    darfst — nicht mehr.
                  </>
                )}
              </p>
              <div class="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  class="rounded px-3 py-1.5 text-sm text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
                >
                  Abbrechen
                </button>
                <Button
                  variant="primary"
                  size="lg"
                  type="button"
                  onClick={() => void create()}
                  disabled={creating || !name.trim()}
                >
                  {creating ? "Erstellt…" : "Erstellen"}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
